import { PAYMENT_TYPES } from '../../../constants/widget-constants';

/**
 * Resolves which payment method the checkout should pre-select and open.
 *
 * Precedence:
 * 1. The partner's configured default — only if it is enabled
 * 2. The customer's last used method — only if it is still enabled, and never Adyen: selecting
 *    Adyen mounts Drop-in, which mints a payable session for this amount, so restoring it from a
 *    previous visit would open a payment the customer has not asked for on this one
 * 3. The first enabled method, when the partner configured a default that is
 *    currently switched off — they asked for a pre-selection, so they get one
 * 4. The single enabled method, when exactly one exists and it is one of the
 *    offline types (invoice, external invoice, store, lexoffice invoice) — an
 *    online gateway is never auto-selected for a partner who configured nothing,
 *    since that would submit the customer to it with no action on their part
 * 5. The on-site ("store") fallback when store payment is enabled
 *
 * Returns null when nothing can be pre-selected.
 */
export function resolvePreselectedPaymentType(
  lastUsed: string | null | undefined,
  configuredDefault: string | null | undefined,
  enabledMethods: string[],
  isStoreEnabled: boolean
): string | null {
  if (configuredDefault && enabledMethods.includes(configuredDefault)) {
    return configuredDefault;
  }

  if (lastUsed && lastUsed !== PAYMENT_TYPES.ADYEN && enabledMethods.includes(lastUsed)) {
    return lastUsed;
  }

  if (configuredDefault && enabledMethods.length) {
    return enabledMethods[0];
  }

  const autoSelectSingleTypes: string[] = [
    PAYMENT_TYPES.INVOICE,
    PAYMENT_TYPES.EXTERNAL_INVOICE,
    PAYMENT_TYPES.STORE,
    PAYMENT_TYPES.LEX_OFFICE_INVOICE
  ];
  if (enabledMethods.length === 1 && autoSelectSingleTypes.includes(enabledMethods[0])) {
    return enabledMethods[0];
  }

  if (isStoreEnabled) {
    return PAYMENT_TYPES.STORE;
  }

  return null;
}

/**
 * Whether the checkout shows the payment methods as cards (one open, the rest
 * behind a link) instead of the dropdown.
 *
 * Only one situation qualifies: a partner who configured a default that is
 * actually enabled. A partner who configured nothing keeps the dropdown, so
 * their checkout is unchanged and their online methods stay visible.
 */
export function shouldUseCardLayout(
  configuredDefault: string | null | undefined,
  enabledMethods: string[]
): boolean {
  return !!configuredDefault && enabledMethods.includes(configuredDefault);
}
